import { CheckCircle2 } from 'lucide-react';
import { textIn, type Lang } from '@rx-manager/forms';
import { useI18n } from '../../lib/i18n';
import { Button } from '../../components/ui';
import { formLang, type FormEntry } from './booking-forms';

/**
 * Track SFM rev 2 — "Your answers" on the booking's review: each form answered or skipped, with
 * Change. The answers themselves are not repeated: each form had its own "Check your answers".
 */
export function FormsSummary({
  entries,
  pageLang,
  pharmacy,
  note,
  skippedText,
  onChange,
}: {
  entries: FormEntry[];
  pageLang: Lang;
  pharmacy: string;
  /** Track CIQ — where the answers go when it isn't a booking (a walk-in joining the line). */
  note?: string;
  /** Track CIQ — a walk-in's skipped form: no link is sent afterwards. */
  skippedText?: string;
  onChange: (index: number) => void;
}) {
  const { t } = useI18n();
  return (
    <section aria-labelledby="review-forms" className="space-y-3">
      <h2 id="review-forms" className="text-lg font-semibold text-heading">
        {t('review.forms')}
      </h2>
      <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
        {entries.map((entry, index) => {
          const lang = formLang(entry.form, pageLang);
          const title = textIn(entry.form.schema.title, lang);
          const answered = entry.status === 'done';
          return (
            <li key={entry.form.formId} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <p lang={lang !== pageLang ? lang : undefined} className="text-base font-semibold text-heading">
                  {title}
                </p>
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  {answered && <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />}
                  {answered ? t('review.formAnswered') : (skippedText ?? t('review.formSkipped'))}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                aria-label={t('review.formChange', { form: title })}
                onClick={() => onChange(index)}
              >
                {t('review.change')}
              </Button>
            </li>
          );
        })}
      </ul>
      <p className="text-sm text-muted-foreground">{note ?? t('review.formsGo', { pharmacy })}</p>
    </section>
  );
}
